import React, { useState, useEffect } from "react";
import { useLocation, useRoute } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import {
  Trophy,
  Users,
  Calendar,
  UserPlus,
  PlayCircle,
  Loader2,
  Check,
  Edit,
  Save,
  X,
  Eye,
  Play,
  RefreshCw,
  User,
  CircleChevronRight,
  Gamepad,
  ChevronRight,
  Clock,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { TournamentBracket } from "@/components/TournamentBracket";
import { InteractiveTournamentBracket } from "@/components/InteractiveTournamentBracket";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { MobileHeader } from "@/components/ui/mobile-header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { EnhancedHeader } from "@/components/ui";
import { TournamentRulesEditor } from "@/components/ui/TournamentRulesEditor";
import PullToRefresh from "react-simple-pull-to-refresh";

// Helper function to safely parse tournament participants
function safelyParseParticipants(participants: any): number[] {
  try {
    if (Array.isArray(participants)) {
      return participants;
    }

    if (typeof participants === "string") {
      const parsed = JSON.parse(participants ?? "[]");
      return Array.isArray(parsed) ? parsed : [];
    }

    return [];
  } catch (error) {
    console.error("Error parsing participants:", error);
    return [];
  }
}
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Define tournament edit schema for form validation
const editTournamentSchema = z.object({
  name: z.string().min(3, "Name must be at least 3 characters"),
  description: z.string().optional(),
  maxPlayers: z
    .number()
    .min(4, "Minimum 4 players required")
    .max(32, "Maximum 32 players allowed"),
  startDate: z.string().optional(),
});

type EditTournamentForm = z.infer<typeof editTournamentSchema>;

// Define the form component for editing a tournament
function EditTournamentForm({
  form,
  onSubmit,
  isPending,
}: {
  form: any;
  onSubmit: (data: EditTournamentForm) => void;
  isPending: boolean;
}) {
  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
      <div className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="name" className="block text-sm font-medium">
            Tournament Name
          </label>
          <Input
            id="name"
            {...form.register("name")}
            className="w-full"
            placeholder="Enter tournament name"
          />
          {form.formState.errors.name && (
            <p className="text-sm text-red-500">
              {form.formState.errors.name.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="description" className="block text-sm font-medium">
            Description (Optional)
          </label>
          <Input
            id="description"
            {...form.register("description")}
            className="w-full"
            placeholder="Enter tournament description"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="maxPlayers" className="block text-sm font-medium">
            Maximum Players
          </label>
          <Controller
            control={form.control}
            name="maxPlayers"
            render={({ field }) => (
              <Select
                onValueChange={(value) => field.onChange(Number(value))}
                value={field.value?.toString()}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select max players" />
                </SelectTrigger>
                <SelectContent>
                  {[4, 8, 16, 32].map((num) => (
                    <SelectItem key={num} value={num.toString()}>
                      {num} players
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {form.formState.errors.maxPlayers && (
            <p className="text-sm text-red-500">
              {form.formState.errors.maxPlayers.message}
            </p>
          )}
        </div>
      </div>

      <Button
        type="submit"
        variant="default"
        className="w-full"
        disabled={isPending}
      >
        {isPending ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <Save className="mr-2 h-4 w-4" />
            Save Changes
          </>
        )}
      </Button>
    </form>
  );
}

export default function TournamentDetailPage() {
  const [_, setLocation] = useLocation();
  const [, params] = useRoute("/tournament/:id");
  const tournamentId = params?.id;
  const { user, token } = useAuth();
  const { toast } = useToast();
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [pendingInvitationsOpen, setPendingInvitationsOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedFriendId, setSelectedFriendId] = useState<number | null>(null);

  // States for editing match winners
  const [editWinnerDialogOpen, setEditWinnerDialogOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<{
    id: number;
    player1Id: number;
    player2Id: number;
    player1Name?: string;
    player2Name?: string;
  } | null>(null);
  const [selectedWinnerId, setSelectedWinnerId] = useState<number | null>(null);
  const [showInteractive, setShowInteractive] = useState(false);

  // Fetch tournament data
  // Mutation for recreating a match game - track which match is being recreated
  const [recreatingMatchId, setRecreatingMatchId] = useState<number | null>(
    null,
  );

  const recreateMatchGameMutation = useMutation({
    mutationFn: async (matchId: number) => {
      // Set the match ID that's being recreated
      setRecreatingMatchId(matchId);

      try {
        const res = await apiRequest(
          "POST",
          `/api/tournaments/${tournamentId}/matches/${matchId}/recreate`,
        );

        if (!res.ok) {
          const error = await res.json();
          throw new Error(error.message ?? "Failed to recreate match game");
        }

        return await res.json();
      } catch (error) {
        console.error("Error in recreateMatchGameMutation:", error);
        throw error;
      }
    },
    onSuccess: async (data) => {
      console.log("Match recreation success:", data);

      toast({
        title: "Success",
        description: "Match game has been recreated",
      });

      // Invalidate tournament data and wait for refetch to complete before navigating
      await queryClient.invalidateQueries({
        queryKey: ["tournament", tournamentId],
      });

      // Force an immediate refetch to ensure UI is updated
      await refetch();

      // Add a short delay to ensure the UI is refreshed
      setTimeout(() => {
        // Safe navigation to game room with error handling
        try {
          if (data?.gameRoom?.id) {
            // Navigate to the game room
            setLocation(`/game/${data.gameRoom.id}`);
          } else {
            console.warn("Game room data is incomplete:", data);
            // Don't navigate, just stay on the current page
          }
        } catch (e) {
          console.error("Error navigating to game room:", e);
        }

        // Reset the recreating match ID
        setRecreatingMatchId(null);
      }, 300); // Short delay to ensure UI updates
    },
    onError: (error: Error) => {
      console.error("Match recreation error:", error);

      toast({
        title: "Error",
        description: error.message ?? "Failed to recreate match game",
        variant: "destructive",
      });

      // Reset the recreating match ID on error too
      setRecreatingMatchId(null);
    },
  });

  const {
    data: tournamentData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["tournament", tournamentId],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/tournaments/${tournamentId}`);
      if (!res.ok) throw new Error("Failed to fetch tournament");

      const tournamentData = await res.json();

      // Create a set of all user IDs we need to fetch (participants + creator)
      const userIdsToFetch = new Set<number>();

      // Add tournament creator
      if (tournamentData.tournament && tournamentData.tournament.creator) {
        userIdsToFetch.add(tournamentData.tournament.creator);
      }

      // Add all participants
      if (tournamentData.tournament && tournamentData.tournament.participants) {
        const participants = safelyParseParticipants(
          tournamentData.tournament.participants,
        );
        participants.forEach((id) => userIdsToFetch.add(id));
      }

      // Fetch all user data in parallel
      if (userIdsToFetch.size > 0) {
        const userPromises = Array.from(userIdsToFetch).map(
          async (id: number) => {
            try {
              const userRes = await apiRequest("GET", `/api/users/${id}`);
              if (userRes.ok) {
                const userData = await userRes.json();
                return { id, username: userData.username };
              }
              return { id, username: `Player #${id}` };
            } catch (error) {
              return { id, username: `Player #${id}` };
            }
          },
        );

        const userDetails = await Promise.all(userPromises);

        // Set creator name
        if (tournamentData.tournament && tournamentData.tournament.creator) {
          const creatorDetail = userDetails.find(
            (u) => u.id === tournamentData.tournament.creator,
          );
          if (creatorDetail) {
            tournamentData.tournament.creatorName = creatorDetail.username;
          }
        }

        // Set participant details
        tournamentData.participantDetails = userDetails;
      }

      return tournamentData;
    },
    enabled: !!tournamentId,
  });

  // Fetch friends list for invitations
  const { data: friendsList } = useQuery({
    queryKey: ["friends"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/friends");
      if (!res.ok) throw new Error("Failed to fetch friends");
      return res.json();
    },
  });

  // Fetch pending invitations (only for the tournament creator)
  const { data: pendingInvitations, refetch: refetchInvitations } = useQuery({
    queryKey: ["tournament-invitations", tournamentId],
    queryFn: async () => {
      const res = await apiRequest(
        "GET",
        `/api/tournaments/${tournamentId}/invitations`,
      );
      if (!res.ok) {
        if (res.status === 403) {
          return []; // Not the tournament creator
        }
        throw new Error("Failed to fetch invitations");
      }
      return res.json();
    },
    enabled: !!tournamentId && !!user,
  });

  // Host leave tournament mutation (uses the same endpoint as removing a participant)
  const leaveTournamentMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error("User not logged in");

      const res = await apiRequest(
        "DELETE",
        `/api/tournaments/${tournamentId}/participants/${user.id}`,
      );
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message ?? "Failed to leave tournament");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "You have left the tournament",
        duration: 3000,
      });

      // Refresh the tournament data
      queryClient.invalidateQueries({ queryKey: ["tournament", tournamentId] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Fetch user's own invitations to check if they've already requested to join
  const { data: userInvitations } = useQuery({
    queryKey: ["user-tournament-invitations"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/tournaments/invitations");
      if (!res.ok) throw new Error("Failed to fetch user invitations");
      return res.json();
    },
    enabled: !!user,
  });

  // Filter friends who are not already in the tournament and don't have pending invitations
  const availableFriends = friendsList?.filter((friend: any) => {
    if (friend.status !== "accepted") return false;

    // Get tournament participants
    const tournament = tournamentData?.tournament;
    if (!tournament) return true;

    // Safely parse the participants array or use an empty array
    const participants = safelyParseParticipants(tournament.participants);

    const friendId =
      friend.userId === user?.id ? friend.friendId : friend.userId;

    // Check if friend is already a participant
    if (participants.includes(friendId)) return false;

    // Check if friend already has a pending invitation
    if (pendingInvitations && pendingInvitations.length > 0) {
      const alreadyInvited = pendingInvitations.some(
        (invitation: any) => invitation.inviteeId === friendId,
      );
      if (alreadyInvited) return false;
    }

    return true;
  });

  // Start tournament mutation
  const startTournamentMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        `/api/tournaments/${tournamentId}/start`,
      );
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message ?? "Failed to start tournament");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Tournament started successfully",
        duration: 3000,
      });
      queryClient.invalidateQueries({ queryKey: ["tournament", tournamentId] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Invite friend mutation
  const inviteFriendMutation = useMutation({
    mutationFn: async (data: { tournamentId: number; inviteeId: number }) => {
      const res = await apiRequest("POST", "/api/tournaments/invite", data);
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message ?? "Failed to invite friend");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Invitation sent successfully",
        duration: 3000,
      });
      setInviteDialogOpen(false);
      setSelectedFriendId(null);
      // Refresh pending invitations
      refetchInvitations();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Cancel/decline invitation mutation
  const cancelInvitationMutation = useMutation({
    mutationFn: async (invitationId: number) => {
      console.log("Declining invitation with ID:", invitationId);
      const res = await apiRequest(
        "PUT",
        `/api/tournaments/invitations/${invitationId}`,
        {
          response: "decline",
        },
      );
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message ?? "Failed to decline invitation");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Invitation declined successfully",
        duration: 3000,
      });
      // Refresh all relevant data
      refetch();
      refetchInvitations();
      queryClient.invalidateQueries({ queryKey: ["tournaments"] });
      queryClient.invalidateQueries({
        queryKey: ["user-tournament-invitations"],
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Accept tournament invitation/join request mutation
  const acceptInvitationMutation = useMutation({
    mutationFn: async ({ invitationId }: { invitationId: number }) => {
      console.log("Accepting invitation with ID:", invitationId);
      const res = await apiRequest(
        "PUT",
        `/api/tournaments/invitations/${invitationId}`,
        { response: "accept" },
      );
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message ?? "Failed to accept invitation");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "User added to tournament successfully",
        duration: 3000,
      });
      // Refresh all relevant tournament data
      refetch();
      refetchInvitations();
      queryClient.invalidateQueries({ queryKey: ["tournaments"] });
      queryClient.invalidateQueries({
        queryKey: ["user-tournament-invitations"],
      });
      queryClient.invalidateQueries({ queryKey: ["tournament", tournamentId] });

      // Redirect to the tournaments page with a slight delay to ensure data is refreshed
      setTimeout(() => {
        setLocation(`/tournament/${tournamentId}`);
      }, 300);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Request to join tournament mutation
  const requestToJoinMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/tournaments/join", {
        tournamentId: Number(tournamentId),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(
          error.message ?? "Failed to request to join tournament",
        );
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Request to join tournament submitted successfully",
        duration: 3000,
      });
      // Invalidate both tournament and user invitations queries to update UI
      queryClient.invalidateQueries({ queryKey: ["tournament", tournamentId] });
      queryClient.invalidateQueries({
        queryKey: ["user-tournament-invitations"],
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update match result mutation
  const updateMatchResultMutation = useMutation({
    mutationFn: async ({
      matchId,
      winnerId,
      gameRoomId,
    }: {
      matchId: number;
      winnerId: number;
      gameRoomId?: number;
    }) => {
      const res = await apiRequest(
        "PUT",
        `/api/tournaments/${tournamentId}/matches/${matchId}`,
        {
          winnerId,
          gameRoomId,
        },
      );
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message ?? "Failed to update match result");
      }
      return res.json();
    },
    onSuccess: (data, variables) => {
      console.log("Match update success:", data);

      // Show different messages based on what we're updating
      if (variables.winnerId) {
        toast({
          title: "Success",
          description: "Match result updated successfully",
          duration: 3000,
        });
      } else if (variables.gameRoomId) {
        toast({
          title: "Success",
          description: "Game room created successfully",
          duration: 3000,
        });
      }

      // Immediately refetch to ensure we have the latest data
      refetch();

      // Invalidate tournament queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ["tournament", tournamentId] });

      // Check if winner was changed and impacted next matches
      // This will show in the impactedMatches array from the server response
      if (data?.match?.winnerId && data?.impactedMatches?.length > 0) {
        console.log(
          "Winner change impacted next round matches:",
          data.impactedMatches,
        );
        toast({
          title: "Next Round Updated",
          description: "The bracket has been updated with the new winner",
          duration: 3000,
        });
      }

      // If next round matches were generated, force additional refresh
      if (data?.nextRoundMatches) {
        console.log("Next round matches generated:", data.nextRoundMatches);
        // Force another refetch to ensure we have the latest tournament state
        setTimeout(() => {
          refetch();
          queryClient.invalidateQueries({
            queryKey: ["tournament", tournamentId],
          });
        }, 500);
      }
    },
    onError: (error: Error) => {
      // Hide the error message about tournament creator permissions
      // and handle other errors with a user-friendly message
      if (
        error.message.includes(
          "Only the tournament creator can update match results",
        )
      ) {
        // We've already implemented the fix to prevent this from happening in normal flow
        console.log("Permission error handled silently:", error.message);
      } else {
        toast({
          title: "Unable to Update",
          description:
            "There was a problem updating the match. Please try again later.",
          variant: "destructive",
        });
      }
    },
  });

  // Remove participant mutation
  const removeParticipantMutation = useMutation({
    mutationFn: async (participantId: number) => {
      const res = await apiRequest(
        "DELETE",
        `/api/tournaments/${tournamentId}/participants/${participantId}`,
      );
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message ?? "Failed to remove participant");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Participant removed successfully",
        duration: 3000,
      });
      queryClient.invalidateQueries({ queryKey: ["tournament", tournamentId] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Edit tournament mutation
  const editTournamentMutation = useMutation({
    mutationFn: async (data: EditTournamentForm) => {
      const res = await apiRequest("PUT", `/api/tournaments/${tournamentId}`, {
        ...data,
        maxPlayers: Number(data.maxPlayers),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message ?? "Failed to update tournament");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Tournament updated successfully",
        duration: 3000,
      });
      setEditDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["tournament", tournamentId] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Initialize form with default values
  const form = useForm<EditTournamentForm>({
    resolver: zodResolver(editTournamentSchema),
    defaultValues: {
      name: "",
      description: "",
      maxPlayers: 8,
      startDate: "",
    },
  });

  // Handle form submission
  const onSubmit = (data: EditTournamentForm) => {
    // Ensure data formats are correctly handled
    const formattedData = {
      ...data,
      maxPlayers: Number(data.maxPlayers),
      // For the API, keep startDate as a string or undefined (not null)
      startDate: data.startDate || undefined,
    };
    editTournamentMutation.mutate(formattedData);
  };

  // Get tournament data
  const tournament = tournamentData?.tournament;
  const matches = tournamentData?.matches ?? [];

  // Reset form values when tournament data changes or edit dialog opens
  useEffect(() => {
    if (tournament && editDialogOpen) {
      form.reset({
        name: tournament.name,
        description: tournament.description || "",
        maxPlayers: tournament.maxPlayers,
        startDate: tournament.startDate || "",
      });
    }
  }, [tournament, editDialogOpen, form]);

  // Handle inviting a friend
  const handleInviteFriend = () => {
    if (!selectedFriendId) {
      toast({
        title: "Error",
        description: "Please select a friend to invite",
        variant: "destructive",
      });
      return;
    }

    inviteFriendMutation.mutate({
      tournamentId: Number(tournamentId),
      inviteeId: selectedFriendId,
    });
  };

  // Handle pull-to-refresh
  const handleRefresh = async () => {
    // Refresh all relevant queries for the tournament detail page
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["tournament", tournamentId] }),
      queryClient.invalidateQueries({ queryKey: ["tournament-invitations", tournamentId] }),
      queryClient.invalidateQueries({ queryKey: ["user-tournament-invitations"] }),
      queryClient.invalidateQueries({ queryKey: ["friends"] }),
    ]);

    toast({
      title: "Refreshed",
      description: "Tournament data has been updated",
      duration: 2000,
    });
    
    return Promise.resolve();
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center items-center h-screen">
        <Loader2 className="h-12 w-12 animate-spin theme-text-primary" />
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h2 className="text-xl font-bold mb-4 theme-text-primary">
            Tournament not found
          </h2>
          <Button onClick={() => setLocation("/tournaments")}>
            Back to Tournaments
          </Button>
        </div>
      </div>
    );
  }

  // Safely parse the participants array
  const participants = safelyParseParticipants(tournament.participants);

  const isCreator = tournament?.creator === user?.id;
  const isPending = tournament?.status === "pending";
  const isActive = tournament?.status === "active";
  const isCompleted = tournament?.status === "completed";
  const isParticipant = participants.includes(user?.id ?? 0);

  // Check if the user has already requested to join this tournament
  const hasRequestedToJoin = userInvitations?.some(
    (invitation: any) =>
      invitation.tournamentId === Number(tournamentId) &&
      invitation.status === "pending" &&
      invitation.inviterId === user?.id &&
      invitation.inviteeId === user?.id,
  );

  // Check if the user has been invited to this tournament
  const hasBeenInvited = userInvitations?.some(
    (invitation: any) =>
      invitation.tournamentId === Number(tournamentId) &&
      invitation.status === "pending" &&
      invitation.inviterId !== user?.id &&
      invitation.inviteeId === user?.id,
  );

  // Get the invitation if the user has been invited
  const pendingInvitation = hasBeenInvited
    ? userInvitations?.find(
        (invitation: any) =>
          invitation.tournamentId === Number(tournamentId) &&
          invitation.status === "pending" &&
          invitation.inviterId !== user?.id &&
          invitation.inviteeId === user?.id,
      )
    : null;

  const canStartTournament = isCreator && isPending && participants.length >= 4;

  return (
    
      <div className="container mx-auto px-4 pb-10">


       <EnhancedHeader
          title='Tournament Details'
          backPath="/tournaments"
          coloredText={true}
          rightContent={isCreator && isPending ? (
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1"
              onClick={() => setEditDialogOpen(true)}
            >
              <Edit className="h-3.5 w-3.5" />
              <span className="font-medium text-sm">Edit</span>
            </Button>
          ) : null}
          showBackButton={true}
        />

    <PullToRefresh 
      onRefresh={handleRefresh}
      pullingContent={
        <div className="flex justify-center pt-4 pb-3 theme-text-muted">
          <Loader2 className="animate-spin h-5 w-5 mr-2" /> Pull to refresh...
        </div>
      }
      refreshingContent={
        <div className="flex justify-center pt-4 pb-3 theme-text-primary">
          <Loader2 className="animate-spin h-5 w-5 mr-2" /> Refreshing...
        </div>
      }
    >
      <div className="pt-4">



      {/* Tournament Header */}
      <Card className="theme-card mb-6 overflow-hidden">
        <CardHeader className="pb-3">
          <div className="space-y-3">
            {/* Title Row */}
            <div className="flex items-center gap-2">
              <div className="rounded-full p-1.5 theme-bg-primary/20 flex items-center justify-center flex-shrink-0">
                <Trophy className="h-4 w-4 theme-text-primary" />
              </div>
              <CardTitle className="text-lg font-semibold">
                {tournament.name}
              </CardTitle>
            </div>
            
            {/* Description */}
            {tournament.description && (
              <p className="text-sm theme-text-muted">
                {tournament.description}
              </p>
            )}
          </div>
        </CardHeader>

        {/* Tournament Stats */}
        <CardContent className="pb-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col">
              <h3 className="text-xs font-medium theme-text-muted mb-1">
                Participants
              </h3>
              <div className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5 theme-text-primary flex-shrink-0" />
                <span className="font-semibold text-sm">
                  {participants.length}/{tournament.maxPlayers}
                </span>
              </div>
            </div>

            

            <div className="flex flex-col">
              <h3 className="text-xs font-medium theme-text-muted mb-1">
                Created By
              </h3>
              <div className="flex items-center gap-1.5">
                <User className="h-3.5 w-3.5 theme-text-primary flex-shrink-0" />
                <span className="font-semibold text-sm truncate">
                  {tournament.creatorName || "Unknown"}
                  {isCreator && <span className="text-xs ml-1 opacity-70">(You)</span>}
                </span>
              </div>
            </div>

            <div className="flex flex-col">
              <h3 className="text-xs font-medium theme-text-muted mb-1">
                {isPending ? "Created" : isActive ? "Started" : "Completed"}
              </h3>
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5 theme-text-primary flex-shrink-0" />
                <span className="font-semibold text-sm">
                  {new Date(
                    isPending
                      ? tournament.createdAt
                      : isActive
                        ? tournament.startDate || tournament.createdAt
                        : tournament.endDate || tournament.createdAt,
                  ).toLocaleDateString()}
                </span>
              </div>
            </div>

            {tournament.currentRound > 0 && (
              <div className="flex flex-col">
                <h3 className="text-xs font-medium theme-text-muted mb-1">
                  Current Round
                </h3>
                <div className="flex items-center gap-1.5">
                  <CircleChevronRight className="h-3.5 w-3.5 theme-text-primary flex-shrink-0" />
                  <span className="font-semibold text-sm">
                    {tournament.currentRound === matches?.length ? "Final" : `Round ${tournament.currentRound}`}
                  </span>
                </div>
              </div>
            )}

            <div className="flex flex-col">
              <h3 className="text-xs font-medium theme-text-muted mb-1">
                
              </h3>
              <div className="flex items-center gap-1.5">
                <Badge
                  variant={isActive ? "default" : isPending ? "outline" : "secondary"}
                  className={`text-xs px-2 py-0.5 ${
                    isActive
                      ? "theme-bg-primary theme-text-contrast"
                      : isPending 
                        ? "bg-yellow-500/10 text-yellow-600 border-yellow-200" 
                        : "bg-gray-500/10 text-gray-600 border-gray-200"
                  }`}
                >
                  {isActive ? "Active" : isPending ? "Upcoming" : "Completed"}
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>

        {isPending && (
          <>
            <Separator />
            {isCreator ? (
              <CardFooter className="pt-4 flex-col items-center gap-3">
                <div className="flex flex-col w-full gap-3">
                  {/* Only show invite button if tournament isn't full */}
                  {participants.length < tournament.maxPlayers && (
                    <Button
                      variant="outline"
                      onClick={() => setInviteDialogOpen(true)}
                      className="flex items-center justify-center w-full"
                      disabled={inviteFriendMutation.isPending}
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      <span className="font-medium">Invite Friends</span>
                    </Button>
                  )}

                  {/* Join Tournament button for the host if not already a participant */}
                  {!isParticipant &&
                    participants.length < tournament.maxPlayers && (
                      <Button
                        variant="default"
                        onClick={() => {
                          // Create a self-invitation that's automatically accepted
                          if (user && user.id) {
                            const userId = Number(user.id);
                            inviteFriendMutation.mutate(
                              {
                                tournamentId: Number(tournamentId),
                                inviteeId: userId,
                              },
                              {
                                onSuccess: (data) => {
                                  // Auto-accept the invitation
                                  if (data && data.id) {
                                    acceptInvitationMutation.mutate({
                                      invitationId: data.id,
                                    });
                                  }
                                },
                              },
                            );
                          }
                        }}
                        className="flex items-center justify-center w-full bg-green-600 hover:bg-green-700"
                        disabled={
                          inviteFriendMutation.isPending ||
                          acceptInvitationMutation.isPending
                        }
                      >
                        <Trophy className="h-4 w-4 mr-2" />
                        <span className="font-medium">Join Tournament</span>
                      </Button>
                    )}

                  {/* Leave Tournament button - only show if the user is a participant */}
                  {isParticipant && (
                    <Button
                      onClick={() => leaveTournamentMutation.mutate()}
                      variant="outline"
                      className="w-full font-medium text-red-500 hover:bg-red-50 hover:text-red-600 border-red-200"
                      disabled={leaveTournamentMutation.isPending}
                    >
                      {leaveTournamentMutation.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          <span className="font-medium">Leaving...</span>
                        </>
                      ) : (
                        <>
                          <X className="mr-2 h-4 w-4" />
                          <span className="font-medium">Leave Tournament</span>
                        </>
                      )}
                    </Button>
                  )}

                  {/* Start Tournament button */}
                  <Button
                    onClick={() => startTournamentMutation.mutate()}
                    variant="default"
                    className="w-full font-medium"
                    disabled={
                      !canStartTournament || startTournamentMutation.isPending
                    }
                  >
                    {startTournamentMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        <span className="font-medium">Starting...</span>
                      </>
                    ) : (
                      <>
                        <Play className="h-4 w-4 mr-2" />
                        <span className="font-medium">Start Tournament</span>
                      </>
                    )}
                  </Button>

                  {/* Show combined invitations and requests management button */}
                  {pendingInvitations && pendingInvitations.length > 0 && (
                    <Button
                      variant="outline"
                      onClick={() => setPendingInvitationsOpen(true)}
                      className="relative w-full flex items-center justify-center"
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      <span className="font-medium">Manage Invitations</span>
                      <span className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center rounded-full text-xs font-medium bg-primary text-primary-foreground">
                        {pendingInvitations.length}
                      </span>
                    </Button>
                  )}
                </div>
              </CardFooter>
            ) : (
              <CardFooter className="pt-4 flex justify-center">
                {/* When the user has been invited to join the tournament */}
                {hasBeenInvited && pendingInvitation && (
                  <div className="flex flex-col sm:flex-row gap-3 items-center w-full sm:w-auto">
                    <div className="w-full sm:w-auto text-center mb-2 sm:mb-0">
                      <Badge className="py-2 px-4 bg-blue-500 text-white">
                        <span className="font-medium">
                          You've been invited to join
                        </span>
                      </Badge>
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto">
                      <Button
                        onClick={() =>
                          acceptInvitationMutation.mutate({
                            invitationId: pendingInvitation.id,
                          })
                        }
                        variant="default"
                        className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700"
                        disabled={acceptInvitationMutation.isPending}
                      >
                        {acceptInvitationMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Check className="h-4 w-4 mr-2" />
                        )}
                        <span className="font-medium">Accept</span>
                      </Button>
                      <Button
                        onClick={() =>
                          cancelInvitationMutation.mutate(pendingInvitation.id)
                        }
                        variant="outline"
                        className="flex-1 sm:flex-none border-red-200 text-red-600 hover:bg-red-50"
                        disabled={cancelInvitationMutation.isPending}
                      >
                        {cancelInvitationMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <X className="h-4 w-4 mr-2" />
                        )}
                        <span className="font-medium">Decline</span>
                      </Button>
                    </div>
                  </div>
                )}

                {/* When user wants to request to join */}
                {!isParticipant &&
                  !hasRequestedToJoin &&
                  !hasBeenInvited &&
                  participants.length < tournament.maxPlayers && (
                    <Button
                      onClick={() => requestToJoinMutation.mutate()}
                      variant="default"
                      className="w-full sm:w-auto font-medium"
                      disabled={requestToJoinMutation.isPending}
                    >
                      {requestToJoinMutation.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          <span className="font-medium">
                            Sending Request...
                          </span>
                        </>
                      ) : (
                        <>
                          <UserPlus className="h-4 w-4 mr-2" />
                          <span className="font-medium">Request to Join</span>
                        </>
                      )}
                    </Button>
                  )}

                {/* Status badges */}
                {isParticipant && (
                  <Badge className="py-2 px-4 bg-green-500 text-white">
                    <span className="font-medium">You are a participant</span>
                  </Badge>
                )}

                {hasRequestedToJoin && (
                  <Badge className="py-2 px-4 bg-yellow-500 text-white">
                    <span className="font-medium">
                      Request pending approval
                    </span>
                  </Badge>
                )}

                {!isParticipant &&
                  !hasRequestedToJoin &&
                  !hasBeenInvited &&
                  participants.length >= tournament.maxPlayers && (
                    <Badge className="py-2 px-4 bg-red-500 text-white">
                      <span className="font-medium">Tournament is full</span>
                    </Badge>
                  )}

                {/* Show badge for declined request */}
                {userInvitations?.some(
                  (invitation: any) =>
                    invitation.tournamentId === Number(tournamentId) &&
                    invitation.status === "declined" &&
                    invitation.inviterId === user?.id &&
                    invitation.inviteeId === user?.id,
                ) && (
                  <Badge className="py-2 px-4 bg-red-500 text-white">
                    <span className="font-medium">Request declined</span>
                  </Badge>
                )}
              </CardFooter>
            )}
          </>
        )}
      </Card>

      <div className="pb-8">
        {/* Tournament Winner (if completed) */}
        {isCompleted && tournament.winnerUserId && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8"
          >
            <Card className="theme-card border-primary overflow-hidden">
              <CardHeader className="pb-2 bg-primary/10">
                <CardTitle className="text-center flex items-center justify-center gap-2">
                  <Trophy className="h-6 w-6 theme-text-primary" />
                  Tournament Champion
                </CardTitle>
              </CardHeader>
              <CardContent className="py-6 text-center">
                <div className="flex flex-col items-center gap-4">
                  <div className="h-16 w-16 rounded-full bg-primary/20 flex items-center justify-center">
                    <Trophy className="h-8 w-8 theme-text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">
                      {tournament.winnerUserId === user?.id
                        ? "You"
                        : tournamentData?.participantDetails?.find(
                            (p: any) => p.id === tournament.winnerUserId,
                          )?.username || `Player #${tournament.winnerUserId}`}
                    </h3>
                    <p className="text-sm theme-text-muted">Congratulations!</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>

      {/* Tabs for Bracket, Matches, and Tournament Info */}
      <div className="mb-8">
        <Tabs defaultValue="bracket" className="w-full">
          <TabsList className="mb-4 grid w-full grid-cols-3">
            <TabsTrigger value="bracket">Bracket</TabsTrigger>
            <TabsTrigger value="matches">
              {isPending ? "Participants" : isActive ? "Matches" : "Results"}
            </TabsTrigger>
            <TabsTrigger value="info">Tournament Info</TabsTrigger>
          </TabsList>

          {/* Bracket Tab Content */}
          <TabsContent value="bracket" className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold theme-text-primary">
                Tournament Bracket
              </h2>
              <div className="flex space-x-2">
                <Button
                  variant={showInteractive ? "default" : "outline"}
                  size="sm"
                  className={`text-xs px-3 ${showInteractive ? "theme-bg-primary theme-text-contrast" : ""}`}
                  onClick={() => setShowInteractive(!showInteractive)}
                >
                  {showInteractive ? "Simple View" : "Interactive View"}
                </Button>
              </div>
            </div>

            {tournament.currentRound &&
            tournament.currentRound > 0 &&
            matches.length > 0 ? (
              <motion.div 
                className="overflow-hidden rounded-xl theme-border border shadow-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <div className="p-4 bg-primary/5 border-b theme-border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Trophy className="h-4 w-4 theme-text-primary" />
                      <span className="font-medium text-sm">
                        {Math.ceil(Math.log2(tournament.maxPlayers))} Round {tournament.maxPlayers}-Player Bracket
                      </span>
                    </div>
                    <div className="text-xs theme-text-muted">
                      Current Round: {tournament.currentRound}
                    </div>
                  </div>
                </div>
                <div className="p-4 overflow-x-auto">
                  {showInteractive ? (
                    <InteractiveTournamentBracket
                      matches={matches}
                      players={tournamentData?.participantDetails || []}
                      maxRounds={Math.ceil(Math.log2(tournament.maxPlayers))}
                      userId={user?.id}
                      winnerUserId={tournament.winnerUserId}
                      isCreator={isCreator}
                      onEditWinner={(matchId, player1Id, player2Id) => {
                        // Get player names for display in the dialog
                        const player1 =
                          tournamentData?.participantDetails?.find(
                            (p: { id: number; username: string }) =>
                              p.id === player1Id,
                          );
                        const player2 =
                          tournamentData?.participantDetails?.find(
                            (p: { id: number; username: string }) =>
                              p.id === player2Id,
                          );

                        setSelectedMatch({
                          id: matchId,
                          player1Id,
                          player2Id,
                          player1Name:
                            player1?.username || `Player #${player1Id}`,
                          player2Name:
                            player2?.username || `Player #${player2Id}`,
                        });
                        setSelectedWinnerId(null);
                        setEditWinnerDialogOpen(true);
                      }}
                      onMatchClick={(match) => {
                        // Navigate to game room if it exists and is ongoing
                        if (
                          match.gameRoomId &&
                          (match.status === "active" ||
                            match.status === "pending")
                        ) {
                          setLocation(`/game/${match.gameRoomId}`);
                        }
                        // Otherwise, provide options for tournament creators
                        else if (isCreator) {
                          // Get player names for display in the dialog
                          const player1 =
                            tournamentData?.participantDetails?.find(
                              (p: { id: number; username: string }) =>
                                p.id === match.player1Id,
                            );
                          const player2 =
                            tournamentData?.participantDetails?.find(
                              (p: { id: number; username: string }) =>
                                p.id === match.player2Id,
                            );

                          setSelectedMatch({
                            id: match.id,
                            player1Id: match.player1Id,
                            player2Id: match.player2Id,
                            player1Name:
                              player1?.username || `Player #${match.player1Id}`,
                            player2Name:
                              player2?.username || `Player #${match.player2Id}`,
                          });

                          // If match already has a winner, open edit dialog
                          if (match.status === "completed" && match.winnerId) {
                            setSelectedWinnerId(match.winnerId);
                            setEditWinnerDialogOpen(true);
                          }
                        }
                      }}
                    />
                  ) : (
                    <TournamentBracket
                      matches={matches}
                      players={tournamentData?.participantDetails || []}
                      maxRounds={Math.ceil(Math.log2(tournament.maxPlayers))}
                      userId={user?.id}
                      winnerUserId={tournament.winnerUserId}
                      isCreator={isCreator}
                      onEditWinner={(matchId, player1Id, player2Id) => {
                        // Get player names for display in the dialog
                        const player1 = tournamentData?.participantDetails?.find(
                          (p: { id: number; username: string }) =>
                            p.id === player1Id,
                        );
                        const player2 = tournamentData?.participantDetails?.find(
                          (p: { id: number; username: string }) =>
                            p.id === player2Id,
                        );

                        setSelectedMatch({
                          id: matchId,
                          player1Id,
                          player2Id,
                          player1Name:
                            player1?.username || `Player #${player1Id}`,
                          player2Name:
                            player2?.username || `Player #${player2Id}`,
                        });
                        setSelectedWinnerId(null);
                        setEditWinnerDialogOpen(true);
                      }}
                    />
                  )}
                </div>
                
                {tournament.winnerUserId && isCompleted && (
                  <div className="p-3 bg-primary/10 border-t theme-border flex items-center justify-center gap-2">
                    <Trophy className="h-4 w-4 text-primary" />
                    <span className="font-medium text-sm">
                      Winner: {tournament.winnerUserId === user?.id
                      ? "You"
                      : tournamentData?.participantDetails?.find(
                          (p: any) => p.id === tournament.winnerUserId,
                        )?.username || `Player #${tournament.winnerUserId}`}
                    </span>
                  </div>
                )}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="text-center py-16 theme-border border-dashed rounded-xl border"
              >
                <div className="rounded-full bg-primary/10 p-4 w-20 h-20 mx-auto mb-5 flex items-center justify-center">
                  <Trophy className="h-10 w-10 theme-text-primary opacity-80" />
                </div>
                <h3 className="text-lg font-medium theme-text">
                  No bracket yet
                </h3>
                <p className="text-sm theme-text-muted mt-1 max-w-sm mx-auto">
                  {isPending
                    ? "Tournament brackets will be generated when the tournament starts. Ensure at least 4 players have joined to begin."
                    : "This tournament has no matches"}
                </p>
                {isPending && isCreator && participants.length >= 4 && (
                  <Button 
                    onClick={() => startTournamentMutation.mutate()}
                    variant="default"
                    className="mt-4 font-medium"
                    disabled={startTournamentMutation.isPending}
                  >
                    {startTournamentMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        <span className="font-medium">Starting...</span>
                      </>
                    ) : (
                      <>
                        <Play className="h-4 w-4 mr-2" />
                        <span className="font-medium">Start Tournament</span>
                      </>
                    )}
                  </Button>
                )}
              </motion.div>
            )}
          </TabsContent>

          {/* Matches/Participants Tab Content */}
          <TabsContent value="matches" className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold theme-text-primary">
                {isPending
                  ? "Participants"
                  : isActive
                    ? "Current Matches"
                    : "Tournament Results"}
              </h2>

              {isPending && isCreator && participants.length >= 4 && (
                <Button 
                  onClick={() => startTournamentMutation.mutate()}
                  variant="default"
                  size="sm"
                  className="font-medium"
                  disabled={startTournamentMutation.isPending}
                >
                  {startTournamentMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      <span className="font-medium">Starting...</span>
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      <span className="font-medium">Start Tournament</span>
                    </>
                  )}
                </Button>
              )}
            </div>

            {isPending ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {participants.length > 0 ? (
                  participants.map((participantId: number, index: number) => {
                    // Find the participant details with username
                    const participant =
                      tournamentData?.participantDetails?.find(
                        (p: any) => p.id === participantId,
                      );
                    const username =
                      participant?.username || `Player #${participantId}`;
                    const isCurrentUser = participantId === user?.id;

                    return (
                      <motion.div
                        key={participantId}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2, delay: index * 0.05 }}
                      >
                        <Card key={index} className={`theme-card hover:shadow-md transition-shadow ${isCurrentUser ? 'border-primary/50 bg-primary/5' : ''}`}>
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`h-10 w-10 rounded-full ${isCurrentUser ? 'bg-primary/20' : 'bg-primary/10'} flex items-center justify-center theme-text-primary font-medium text-lg`}>
                                  {index + 1}
                                </div>
                                <div>
                                  <div className="font-medium text-base">
                                    {participantId === user?.id ? "You" : username}
                                  </div>
                                  <div className="text-xs theme-text-muted">
                                    Joined {new Date(tournament.createdAt).toLocaleDateString()}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {participantId === user?.id && (
                                  <Badge
                                    variant="outline"
                                    className="bg-primary/10 text-primary border-primary/20"
                                  >
                                    <span className="font-medium">You</span>
                                  </Badge>
                                )}
                                {/* Remove participant button (only visible to the tournament creator) */}
                                {isCreator &&
                                  isPending &&
                                  participantId !== user?.id && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 rounded-full hover:bg-red-100 hover:text-red-600"
                                      onClick={() =>
                                        removeParticipantMutation.mutate(
                                          participantId,
                                        )
                                      }
                                      disabled={
                                        removeParticipantMutation.isPending
                                      }
                                      title="Remove participant"
                                    >
                                      {removeParticipantMutation.isPending ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <X className="h-4 w-4" />
                                      )}
                                    </Button>
                                  )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    );
                  })
                ) : (
                  <div className="col-span-full text-center py-16 theme-border border-dashed rounded-xl border">
                    <div className="rounded-full bg-primary/10 p-4 w-20 h-20 mx-auto mb-5 flex items-center justify-center">
                      <Users className="h-10 w-10 theme-text-primary opacity-80" />
                    </div>
                    <h3 className="text-lg font-medium theme-text mb-1">
                      No participants yet
                    </h3>
                    <p className="text-sm theme-text-muted mb-4 max-w-sm mx-auto">
                      Invite friends to join the tournament or join yourself.
                    </p>
                    <Button 
                      variant="default" 
                      className="font-medium"
                      onClick={() => setInviteDialogOpen(true)}
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      <span className="font-medium">Invite Players</span>
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                {tournament.currentRound && tournament.currentRound > 0 ? (
                  // Display all rounds generated so far, reversed to show newer rounds at the top
                  Array.from({
                    length: Math.max(
                      tournament.currentRound,
                      ...matches.map((m: any) => m.round),
                    ),
                  }).map((_, index) => {
                    // Map index to round number in reverse order
                    // For example, if we have 3 rounds, index 0 → round 3, index 1 → round 2, index 2 → round 1
                    const totalRounds = Math.max(
                      tournament.currentRound,
                      ...matches.map((m: any) => m.round),
                    );
                    const roundNumber = totalRounds - index;
                    const roundMatches = matches.filter(
                      (match: any) => match.round === roundNumber,
                    );

                    // Only render rounds that have matches
                    if (roundMatches.length === 0) return null;

                    return (
                      <motion.div 
                        key={roundNumber} 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="space-y-4"
                      >
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center theme-text-primary">
                            {roundNumber}
                          </div>
                          <h3 className="text-lg font-medium theme-text-primary">
                            {roundNumber === 1
                              ? "First Round"
                              : roundMatches.length === 1 &&
                                  roundNumber ===
                                    Math.max(...matches.map((m: any) => m.round))
                                ? "Final Round"
                                : `Round ${roundNumber}`}
                          </h3>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {roundMatches.map((match: any, matchIndex: number) => (
                            <motion.div
                              key={match.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.2, delay: matchIndex * 0.05 }}
                            >
                              <Card
                                key={match.id}
                                className={`theme-card overflow-hidden ${match.status === "completed" ? "border-green-200" : ""}`}
                              >
                                <div className="p-4">
                                  <div className="flex justify-between items-center mb-3 text-xs">
                                    <div className="flex items-center gap-2">
                                      <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
                                        Match #{match.matchNumber}
                                      </Badge>
                                      {match.status === "completed" && (
                                        <Badge
                                          variant="outline"
                                          className="bg-green-500/10 text-green-600 border-green-200"
                                        >
                                          Completed
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-center justify-between mb-3">
                                    <div className={`flex-1 flex items-center gap-2 ${match.winnerId === match.player1Id ? "font-bold" : ""}`}>
                                      <div className={`h-8 w-8 rounded-full ${match.player1Id === user?.id ? "bg-blue-500/10 text-blue-600" : "bg-primary/10"} flex items-center justify-center`}>
                                        {match.player1Id === user?.id ? "You" : match.player1Id.toString().substring(0, 1)}
                                      </div>
                                      <span className={`${match.winnerId === match.player1Id ? "theme-text-primary" : ""}`}>
                                        {match.player1Id === user?.id
                                          ? "You"
                                          : tournamentData?.participantDetails?.find(
                                              (p: any) => p.id === match.player1Id,
                                            )?.username || `Player #${match.player1Id}`}
                                      </span>
                                      {match.winnerId === match.player1Id && (
                                        <Trophy className="h-4 w-4 text-yellow-500 ml-1" />
                                      )}
                                    </div>

                                    <div className="flex-none px-2 font-medium theme-text-muted">
                                      vs
                                    </div>

                                    <div className={`flex-1 flex items-center gap-2 justify-end ${match.winnerId === match.player2Id ? "font-bold" : ""}`}>
                                      {match.winnerId === match.player2Id && (
                                        <Trophy className="h-4 w-4 text-yellow-500 mr-1" />
                                      )}
                                      <span className={`${match.winnerId === match.player2Id ? "theme-text-primary" : ""}`}>
                                        {match.player2Id === user?.id
                                          ? "You"
                                          : tournamentData?.participantDetails?.find(
                                              (p: any) => p.id === match.player2Id,
                                            )?.username || `Player #${match.player2Id}`}
                                      </span>
                                      <div className={`h-8 w-8 rounded-full ${match.player2Id === user?.id ? "bg-blue-500/10 text-blue-600" : "bg-primary/10"} flex items-center justify-center`}>
                                        {match.player2Id === user?.id ? "You" : match.player2Id.toString().substring(0, 1)}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Allow both 'pending' and 'active' statuses to show action buttons */}
                                {(match.status === "pending" || match.status === "active") && (
                                  <>
                                    <Separator />
                                    <CardFooter className="pt-4 flex flex-col sm:flex-row gap-3">
                                      {/* Tournament Creator Controls */}
                                      {isCreator && (
                                        <div className="w-full sm:w-auto">
                                          <Select
                                            onValueChange={(value) => {
                                              updateMatchResultMutation.mutate({
                                                matchId: match.id,
                                                winnerId: Number(value),
                                              });
                                            }}
                                          >
                                            <SelectTrigger className="w-full sm:w-[180px]">
                                              <SelectValue placeholder="Set Winner" />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {[match.player1Id, match.player2Id].map((playerId) => (
                                                <SelectItem key={playerId} value={playerId.toString()}>
                                                  {playerId === user?.id
                                                    ? "You"
                                                    : tournamentData?.participantDetails?.find(
                                                        (p: any) => p.id === playerId
                                                      )?.username || `Player #${playerId}`}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </div>
                                      )}

                                      {/* Game Room and Match Controls */}
                                      {match.gameRoomId ? (
                                        // Match has a game room
                                        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                                          {/* View/Join Game Button */}
                                          <Button
                                            variant={match.winnerId ? "outline" : "default"}
                                            onClick={() =>
                                              setLocation(
                                                match.winnerId
                                                  ? `/game-history/${match.gameRoomId}`
                                                  : `/game/${match.gameRoomId}`
                                              )
                                            }
                                            className="flex items-center gap-2 w-full sm:w-auto"
                                          >
                                            {match.winnerId ? (
                                              <>
                                                <Eye className="h-4 w-4" />
                                                Watch Replay
                                              </>
                                            ) : (
                                              <>
                                                <PlayCircle className="h-4 w-4" />
                                                {user?.id === match.player1Id || user?.id === match.player2Id
                                                  ? "Join Game"
                                                  : "Watch Match"}
                                                <ChevronRight className="h-4 w-4 ml-1" />
                                              </>
                                            )}
                                          </Button>

                                          {/* Recreate Game Button (only for tournament creator) */}
                                          {isCreator && (
                                            <Button
                                              variant="outline"
                                              onClick={() => {
                                                try {
                                                  if (typeof match.id === "number") {
                                                    recreateMatchGameMutation.mutate(match.id);
                                                  }
                                                } catch (error) {
                                                  console.error("Error in recreate match button:", error);
                                                }
                                              }}
                                              className="flex items-center gap-2 w-full sm:w-auto bg-primary/10 text-primary hover:bg-primary/20"
                                              disabled={recreatingMatchId === match.id}
                                            >
                                              <RefreshCw
                                                className={`h-4 w-4 ${
                                                  recreatingMatchId === match.id ? "animate-spin" : ""
                                                }`}
                                              />
                                              {recreatingMatchId === match.id ? "Creating..." : "Recreate Game"}
                                            </Button>
                                          )}
                                        </div>
                                      ) : (
                                        // No game room yet
                                        <>
                                          {isCreator ? (
                                            // Tournament creator can create game
                                            <Button
                                              variant="default"
                                              onClick={async () => {
                                                try {
                                                  const res = await apiRequest("POST", "/api/game-rooms", {
                                                    name: `Tournament Match #${match.matchNumber}`,
                                                    gameType: "tournament",
                                                    spectatorMode: "tournament",
                                                    tournamentId: Number(tournamentId),
                                                    matchId: match.id,
                                                    creator: user?.id,
                                                  });

                                                  if (!res.ok) {
                                                    throw new Error("Failed to create game room");
                                                  }

                                                  const data = await res.json();

                                                  updateMatchResultMutation.mutate({
                                                    matchId: match.id,
                                                    winnerId: match.winnerId || 0,
                                                    gameRoomId: data.id,
                                                  });

                                                  setLocation(`/game/${data.id}`);
                                                } catch (error: any) {
                                                  toast({
                                                    title: "Error",
                                                    description: error.message,
                                                    variant: "destructive",
                                                  });
                                                }
                                              }}
                                              className="flex items-center gap-2 w-full sm:w-auto"
                                            >
                                              <PlayCircle className="h-4 w-4" />
                                              Create Game
                                              <ChevronRight className="h-4 w-4 ml-1" />
                                            </Button>
                                          ) : (
                                            // Non-creators see waiting message
                                            <div className="text-sm italic theme-text-muted px-1">
                                              <Clock className="h-4 w-4 inline mr-1" />
                                              Waiting for tournament host to create game...
                                            </div>
                                          )}
                                        </>
                                      )}
                                    </CardFooter>
                                  </>
                                )}

                                {match.status === "completed" && (
                                  <>
                                    <Separator />
                                    <CardFooter className="pt-4 flex flex-col sm:flex-row gap-3">
                                      <div className="w-full sm:flex-1">
                                        <div className="flex gap-2 items-center">
                                          <Trophy className="h-4 w-4 text-yellow-500" />
                                          <div className="text-sm font-medium">
                                            Winner: {match.winnerId === user?.id
                                              ? "You"
                                              : tournamentData?.participantDetails?.find(
                                                (p: any) => p.id === match.winnerId,
                                              )?.username || `Player #${match.winnerId}`}
                                          </div>
                                        </div>
                                      </div>

                                      {/* Show Watch Match button if there's a game room and match was not awarded manually */}
                                      <div className="w-full sm:w-auto">
                                        {match.gameRoomId &&
                                          !match.manuallyAwarded && (
                                            <Button
                                            variant={match.winnerId ? "outline" : "default"}
                                            onClick={() =>
                                              setLocation(
                                                match.winnerId
                                                  ? `/game-history/${match.gameRoomId}`
                                                  : `/game/${match.gameRoomId}`
                                              )
                                            }
                                            className="flex items-center gap-2 w-full sm:w-auto"
                                          >
                                            {match.winnerId ? (
                                              <>
                                                <Eye className="h-4 w-4" />
                                                Watch Replay
                                              </>
                                            ) : (
                                              <>
                                                <PlayCircle className="h-4 w-4" />
                                                {user?.id === match.player1Id || user?.id === match.player2Id
                                                  ? "Join Game"
                                                  : "Watch Match"}
                                                <ChevronRight className="h-4 w-4 ml-1" />
                                              </>
                                            )}
                                          </Button>
                                          )}

                                        

                                        {/* Show Recreate Game button for tournament hosts */}
                                        {isCreator && (
                                          <Button
                                            variant="default"
                                            onClick={() => {
                                              try {
                                                if (
                                                  match &&
                                                  typeof match.id === "number"
                                                ) {
                                                  recreateMatchGameMutation.mutate(match.id);
                                                } else {
                                                  // ... error handling ...
                                                }
                                              } catch (error) {
                                                console.error(
                                                  "Error in recreate match button:",
                                                  error,
                                                );
                                              }
                                            }}
                                            className="flex items-center gap-2 w-full sm:w-auto mt-2 sm:mt-0 sm:ml-2"
                                            disabled={
                                              recreatingMatchId === match.id
                                            }
                                          >
                                            <RefreshCw
                                              className={`h-4 w-4 ${recreatingMatchId === match.id ? "animate-spin" : ""}`}
                                            />
                                            {recreatingMatchId === match.id
                                              ? "Creating..."
                                              : "Recreate Game"}
                                          </Button>
                                        )}
                                      </div>
                                    </CardFooter>
                                  </>
                                )}
                              </Card>
                            </motion.div>
                          ))}
                        </div>
                      </motion.div>
                    );
                  })
                ) : (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="text-center py-16 theme-border border-dashed rounded-xl border"
                  >
                    <div className="rounded-full bg-primary/10 p-4 w-20 h-20 mx-auto mb-5 flex items-center justify-center">
                      <Trophy className="h-10 w-10 theme-text-primary opacity-80" />
                    </div>
                    <h3 className="text-lg font-medium theme-text mb-1">
                      No matches yet
                    </h3>
                    <p className="text-sm theme-text-muted mb-4 max-w-sm mx-auto">
                      {isActive
                        ? "Matches will be generated soon"
                        : "This tournament has no matches"}
                    </p>
                    {isCreator && isPending && participants.length >= 4 && (
                      <Button 
                        onClick={() => startTournamentMutation.mutate()}
                        variant="default"
                        className="mt-2 font-medium"
                        disabled={startTournamentMutation.isPending}
                      >
                        {startTournamentMutation.isPending ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            <span className="font-medium">Starting...</span>
                          </>
                        ) : (
                          <>
                            <Play className="h-4 w-4 mr-2" />
                            <span className="font-medium">Start Tournament</span>
                          </>
                        )}
                      </Button>
                    )}
                  </motion.div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Tournament Info Tab Content */}
          <TabsContent value="info" className="space-y-4">
            <TournamentRulesEditor 
              tournamentId={tournamentId ?? ""}
              isHost={isCreator}
              initialData={tournament.rules ? {
                rules: tournament.rules,
                prizeDetails: tournament.prizeDetails,
                additionalInfo: tournament.additionalInfo
              } : {}}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Invite Friends Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="sm:max-w-[400px] theme-bg-card">
          <DialogHeader>
            <DialogTitle>Invite Friends</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {availableFriends?.length > 0 ? (
              <Select
                onValueChange={(value) => setSelectedFriendId(Number(value))}
              >
                <SelectTrigger className="w-full theme-bg-secondary/20 theme-border">
                  <SelectValue placeholder="Select a friend" />
                </SelectTrigger>
                <SelectContent>
                  {availableFriends.map((friend: any) => {
                    const friendId =
                      friend.userId === user?.id
                        ? friend.friendId
                        : friend.userId;
                    return (
                      <SelectItem key={friendId} value={friendId.toString()}>
                        {friend.username}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-center theme-text-muted">
                No friends available to invite
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setInviteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleInviteFriend}
              variant="default"
              disabled={inviteFriendMutation.isPending || !selectedFriendId}
            >
              {inviteFriendMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send Invitation"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pending Invitations Dialog */}
      <Dialog
        open={pendingInvitationsOpen}
        onOpenChange={setPendingInvitationsOpen}
      >
        <DialogContent className="sm:max-w-[500px] theme-bg-card">
          <DialogHeader>
            <DialogTitle>
              {pendingInvitations &&
              pendingInvitations.some((inv: any) => inv.isJoinRequest)
                ? "Tournament Join Requests"
                : "Pending Invitations"}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {pendingInvitations && pendingInvitations.length > 0 ? (
              <div className="space-y-4">
                {/* Filter out the join requests first if they exist */}
                {pendingInvitations.some((inv: any) => inv.isJoinRequest) && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium theme-text-primary">
                      Join Requests
                    </h3>
                    {pendingInvitations
                      .filter((invitation: any) => invitation.isJoinRequest)
                      .map((invitation: any) => (
                        <Card
                          key={invitation.id}
                          className="theme-card overflow-hidden border-primary/20"
                        >
                          <CardContent className="py-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                                  <UserPlus className="h-4 w-4 theme-text-primary" />
                                </div>
                                <div>
                                  <div className="font-medium">
                                    {invitation.inviteeName ||
                                      tournamentData?.participantDetails?.find(
                                        (p: any) =>
                                          p.id === invitation.inviteeId,
                                      )?.username ||
                                      `User #${invitation.inviteeId}`}
                                  </div>
                                  <div className="text-xs theme-text-muted">
                                    Requested{" "}
                                    {new Date(
                                      invitation.createdAt,
                                    ).toLocaleDateString()}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    acceptInvitationMutation.mutate({
                                      invitationId: invitation.id,
                                    })
                                  }
                                  disabled={acceptInvitationMutation.isPending}
                                  className="h-8 bg-primary/10 text-primary border-primary/20 hover:bg-primary/20"
                                >
                                  {acceptInvitationMutation.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    "Accept"
                                  )}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() =>
                                    cancelInvitationMutation.mutate(
                                      invitation.id,
                                    )
                                  }
                                  disabled={cancelInvitationMutation.isPending}
                                  className="h-8 w-8 rounded-full hover:bg-destructive/10 hover:text-destructive"
                                >
                                  {cancelInvitationMutation.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <X className="h-4 w-4" />
                                  )}
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                  </div>
                )}

                {/* Regular invitations */}
                {pendingInvitations.some((inv: any) => !inv.isJoinRequest) && (
                  <div className="space-y-3">
                    {pendingInvitations.some(
                      (inv: any) => inv.isJoinRequest,
                    ) && (
                      <h3 className="text-sm font-medium theme-text-primary mt-4">
                        Invitations
                      </h3>
                    )}
                    {pendingInvitations
                      .filter((invitation: any) => !invitation.isJoinRequest)
                      .map((invitation: any) => (
                        <Card
                          key={invitation.id}
                          className="theme-card overflow-hidden"
                        >
                          <CardContent className="py-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                                  <UserPlus className="h-4 w-4 theme-text-primary" />
                                </div>
                                <div>
                                  <div className="font-medium">
                                    {invitation.inviteeName ||
                                      tournamentData?.participantDetails?.find(
                                        (p: any) =>
                                          p.id === invitation.inviteeId,
                                      )?.username ||
                                      `User #${invitation.inviteeId}`}
                                  </div>
                                  <div className="text-xs theme-text-muted">
                                    Invited{" "}
                                    {new Date(
                                      invitation.createdAt,
                                    ).toLocaleDateString()}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center">
                                <Badge
                                  variant="outline"
                                  className="mr-2 bg-background text-white border-accent"
                                >
                                  <span className="font-medium">
                                    {invitation.status}
                                  </span>
                                </Badge>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() =>
                                    cancelInvitationMutation.mutate(
                                      invitation.id,
                                    )
                                  }
                                  disabled={cancelInvitationMutation.isPending}
                                  className="h-8 w-8 rounded-full hover:bg-destructive/10 hover:text-destructive"
                                >
                                  {cancelInvitationMutation.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <X className="h-4 w-4" />
                                  )}
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-10">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <Check className="h-6 w-6 theme-text-primary" />
                </div>
                <h3 className="text-lg font-medium theme-text">
                  No pending invitations
                </h3>
                <p className="text-sm theme-text-muted mt-1">
                  All invitations have been responded to
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPendingInvitationsOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Tournament Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px] theme-bg-card">
          <DialogHeader>
            <DialogTitle>Edit Tournament</DialogTitle>
          </DialogHeader>
          <EditTournamentForm
            form={form}
            onSubmit={onSubmit}
            isPending={editTournamentMutation.isPending}
          />
        </DialogContent>
      </Dialog>
      </div>
          </PullToRefresh>

    </div>
  );
}
